// src/hooks/useJanetSTT.js — Cartesia Ink-Whisper STT for Janet bidirectional voice.
// Phase 2b: MediaRecorder captures audio, server proxies to Cartesia, returns transcript.
// PTT control (fn key, hold-to-talk) lives in JanetFAB — this hook is the audio engine only.
//
// Public API (same shape as before — JanetFAB needs no changes):
//   isListening          — true while MediaRecorder is recording
//   isTranscribing       — true while waiting for Cartesia response
//   transcript           — 'Recording…' → 'Transcribing…' → final text or ''
//   startListening(onFinal)   — open mic, start recording; onFinal(text) called after stop+transcribe
//   stopListening()           — stop recording, trigger async transcription
//   startWakeWord / stopWakeWord / wakeWordActive  — stubs (replaced by Backslash (\) PTT)
//   supported            — false if getUserMedia unavailable

import { useState, useRef, useCallback, useEffect } from 'react'

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = () => resolve(reader.result.split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function transcribeAudio(audioBlob) {
  const base64 = await blobToBase64(audioBlob)
  const res = await fetch('/api/janet-transcribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ audio_base64: base64, mime_type: audioBlob.type || 'audio/webm' }),
  })
  if (!res.ok) throw new Error(`Transcription failed (${res.status})`)
  const data = await res.json()
  return data.transcript || ''
}

export function useJanetSTT() {
  const [isListening,    setIsListening]    = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [transcript,     setTranscript]     = useState('')

  const streamRef   = useRef(null)   // MediaStream
  const recorderRef = useRef(null)   // MediaRecorder
  const chunksRef   = useRef([])
  const onFinalRef  = useRef(null)
  const onErrorRef  = useRef(null)
  const activeRef   = useRef(false)  // prevents stale callbacks after unmount/stop

  const supported = typeof window !== 'undefined' &&
    !!window.navigator?.mediaDevices?.getUserMedia

  // ── Stop (triggers async transcription if audio was captured) ────────────
  const stopListening = useCallback(() => {
    activeRef.current = false
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()  // fires onstop → transcription
    } else {
      // Already stopped — clean up stream manually
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
      setIsListening(false)
    }
  }, [])

  // ── Start ─────────────────────────────────────────────────────────────────
  const startListening = useCallback((onFinal, onError) => {
    if (!supported) {
      onError?.('Microphone not supported in this browser.')
      return
    }

    // Abort any in-flight session
    activeRef.current = false
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }

    onFinalRef.current = onFinal
    onErrorRef.current = onError
    activeRef.current  = true
    chunksRef.current  = []
    setTranscript('Recording…')
    setIsTranscribing(false)

    navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      .then(stream => {
        if (!activeRef.current) {
          stream.getTracks().forEach(t => t.stop())
          return
        }
        streamRef.current = stream

        // Pick the best supported codec
        const mimePrefs = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
        const mimeType  = mimePrefs.find(m => MediaRecorder.isTypeSupported(m)) || ''
        const recorder  = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
        recorderRef.current = recorder

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data)
        }

        recorder.onstop = async () => {
          stream.getTracks().forEach(t => t.stop())
          streamRef.current  = null
          recorderRef.current = null
          setIsListening(false)

          const chunks = chunksRef.current.slice()
          chunksRef.current = []

          if (!chunks.length) { setTranscript(''); return }

          const recordedMime = chunks[0].type || mimeType || 'audio/webm'
          const blob = new Blob(chunks, { type: recordedMime })

          // Bail on sub-second audio — almost certainly a mis-tap
          if (blob.size < 2000) { setTranscript(''); return }

          setIsTranscribing(true)
          setTranscript('Transcribing…')
          try {
            const text = await transcribeAudio(blob)
            setTranscript(text)
            if (text && onFinalRef.current) onFinalRef.current(text.trim())
            else if (!text) setTranscript('')
          } catch (err) {
            console.warn('STT error:', err.message)
            setTranscript('')
            onErrorRef.current?.(`Transcription failed — ${err.message}`)
          } finally {
            setIsTranscribing(false)
          }
        }

        recorder.start()
        setIsListening(true)
      })
      .catch(err => {
        console.warn('Microphone access denied:', err.message)
        activeRef.current = false
        setIsListening(false)
        setTranscript('')
        const msg = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
          ? 'Microphone access denied — check your browser settings.'
          : `Microphone error: ${err.message}`
        onErrorRef.current?.(msg)
      })
  }, [supported])

  // ── Wake-word stubs (replaced by fn key hold-to-talk PTT in JanetFAB) ───────
  const startWakeWord = useCallback(() => {}, [])
  const stopWakeWord  = useCallback(() => {}, [])

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => () => {
    activeRef.current = false
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
    }
  }, [])

  return {
    isListening,
    isTranscribing,
    transcript,
    startListening,
    stopListening,
    startWakeWord,
    stopWakeWord,
    wakeWordActive: false,
    supported,
  }
}
